import React, { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { useGetAttendanceSessionReportQuery } from "../../Api/attoDeskApi";
import { getAvatar } from "../../utils/avatar";
import { StepUpSpinner } from "../../Components/Loader";
import GradeSubjectsDialog, { summarizeGradesCompact } from "../AttendanceSession/GradeSubjectsDialog";
import useUser from "../../Core/hook/useUser";

/**
 * Attendance Session Report — /attendance/report/:id
 *
 * Operational report for a single class occurrence. Reads the enriched
 * DTO from `AttendanceSessionController::report()` — grade_subjects,
 * scheduled/actual timestamps, ended_by, mode, enrolled/late_slip
 * counts and per-student arrival data all come straight from the BE.
 * The trend `submitted` flag lets this page exclude unmarked sessions
 * from the rate calculation so a class that was never marked doesn't
 * render as 0% performance (spec §18).
 */

interface GradeSubjectsBucket {
  grade_id: number;
  grade: { id: number; name: string; sort_order?: number | null };
  subjects: Array<{ id: number; name: string; sort_order?: number | null }>;
}
interface StudentRow {
  id: number;
  name: string;
  grade: string | null;
  avatar: string | null;
  parent_name: string | null;
  status: "present" | "absent" | "late" | "not_marked";
  attendance_rate: number;
  arrived_at?: string | null;
  late_minutes?: number | null;
  late_reason?: string | null;
  has_late_slip?: boolean;
  stats: {
    total: number; present: number; absent: number; late: number;
    this_month_rate: number; last_month_rate: number;
  };
  monthly_stats: Array<{ month: string; percentage: number }>;
  recent_history: Array<{ date: string; class_name: string; status: string }>;
}
interface Report {
  session: {
    id: number; class_id: number;
    class_name: string;
    grade_subjects: GradeSubjectsBucket[];
    date: string;
    start_time: string; end_time: string;
    scheduled_start_at: string; scheduled_end_at: string;
    actual_start_at: string | null; actual_end_at: string | null;
    ended_by: "manual" | "system" | null;
    late_start_minutes: number | null;
    status: "scheduled" | "completed" | "cancelled" | "rescheduled";
    location: string | null;
    mode: string | null;
    staff_name: string | null;
    staff_photo: string | null;
  };
  summary: {
    total: number; enrolled: number;
    present: number; absent: number; late: number;
    not_recorded: number; late_slip_count: number;
    attendance_rate: number;
    submitted: boolean;
  };
  students: StudentRow[];
  class_trend: Array<{ occurrence_id: number; label: string; rate: number; submitted: boolean; isCurrentSession: boolean }>;
  top_students: Array<{ id: number; name: string; grade: string | null; avatar: string | null; attendance_rate: number }>;
  top_classes: Array<{ name: string; grade: string; staff: string; rate: number }>;
}

// ── formatters ─────────────────────────────────────────────────────
const fmtDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
};
const fmtTimeIso = (iso: string | null | undefined) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};
const fmtDuration = (ms: number) => {
  if (ms <= 0) return "0 min";
  const m = Math.round(ms / 60000);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h} hr` : `${h} hr ${rem} min`;
};

/**
 * Shared late-arrival duration formatter for the report page.
 *
 * Rules (from the spec):
 *   null / 0        → null  (caller renders `—`; never "0m")
 *   1–59 minutes    → "X min"
 *   exact hours     → "X hr"
 *   otherwise       → "X hr Y min"
 *
 * Returning null (not "0 min") on the zero case is deliberate: every
 * caller needs to render a muted em-dash in that spot, not the string
 * "0 min", so putting the branch here keeps the rule in one place and
 * makes the "no `0m` anywhere on the page" acceptance criterion easy
 * to enforce by grep.
 */
const formatLateMinutes = (mins: number | null | undefined): string | null => {
  const m = mins ?? 0;
  if (!Number.isFinite(m) || m <= 0) return null;
  const whole = Math.round(m);
  if (whole < 60) return `${whole} min`;
  const h = Math.floor(whole / 60);
  const rem = whole % 60;
  return rem === 0 ? `${h} hr` : `${h} hr ${rem} min`;
};

const statusChip = (s: string) => {
  if (s === "present") return { bg: "#f0fdf4", fg: "#16a34a", border: "#bbf7d0", label: "Present" };
  if (s === "absent")  return { bg: "#fef2f2", fg: "#dc2626", border: "#fecaca", label: "Absent" };
  if (s === "late")    return { bg: "#fffbeb", fg: "#d97706", border: "#fde68a", label: "Late" };
  return { bg: "#f1f5f9", fg: "#64748b", border: "#e2e8f0", label: "Not Recorded" };
};
const rateColor = (r: number) => (r >= 80 ? "#16a34a" : r >= 50 ? "#f59e0b" : "#ef4444");

// ── compact grade summary rules (spec §2, §23) ─────────────────────
const summarizeClassTitle = (session: Report["session"]): string => {
  // Prefer an explicit class_name unless it's the noisy comma-joined
  // grade dump the old snapshot column stores ("1, 2, 3, 4, ..."). If
  // it looks like a snapshot fall through to the compact summary.
  const raw = (session.class_name || "").trim();
  const looksLikeGradeDump = /,.*,.*,/.test(raw); // 3+ commas
  const gs = session.grade_subjects || [];
  if (raw && !looksLikeGradeDump) return raw;
  if (gs.length === 0) return raw || "Class Session";
  const { gradesLabel } = summarizeGradesCompact(gs);
  const gradeCount = gs.length;
  const uniqueSubjects = new Set(gs.flatMap((g) => g.subjects.map((s) => s.id))).size;
  const single = gradeCount === 1 ? gs[0] : null;
  if (single && single.subjects.length <= 3) {
    return `${gradesLabel} — ${single.subjects.map((s) => s.name).join(", ")}`;
  }
  return `${gradeCount} Grade${gradeCount === 1 ? "" : "s"} · ${uniqueSubjects} Subject${uniqueSubjects === 1 ? "" : "s"}`;
};

// Late slip modal — only real fields, never fabricated.
const LateSlipModal: React.FC<{
  student: StudentRow | null;
  session: Report["session"];
  onClose: () => void;
}> = ({ student, session, onClose }) => {
  if (!student) return null;
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(15,23,42,0.35)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 250, padding: 20,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: "100%", maxWidth: 460, background: "#fff",
        borderRadius: 14, boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        padding: 22,
      }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: "#1b3a6b", marginBottom: 2 }}>Late Slip</div>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 16 }}>Details recorded by the teacher</div>
        <div style={{ display: "grid", gridTemplateColumns: "110px 1fr", rowGap: 10, columnGap: 12, fontSize: 13 }}>
          <div style={{ color: "#64748b" }}>Student</div><div style={{ fontWeight: 700 }}>{student.name}</div>
          {student.grade && (<><div style={{ color: "#64748b" }}>Grade</div><div>{student.grade}</div></>)}
          <div style={{ color: "#64748b" }}>Date</div><div>{fmtDate(session.date)}</div>
          <div style={{ color: "#64748b" }}>Class</div><div>{summarizeClassTitle(session)}</div>
          <div style={{ color: "#64748b" }}>Arrival</div><div>{fmtTimeIso(student.arrived_at)}</div>
          {formatLateMinutes(student.late_minutes) && (
            <><div style={{ color: "#64748b" }}>Late by</div><div>{formatLateMinutes(student.late_minutes)}</div></>
          )}
          <div style={{ color: "#64748b" }}>Reason</div>
          <div>{student.late_reason?.trim() || <span style={{ color: "#94a3b8" }}>—</span>}</div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
          <button onClick={onClose} style={{
            background: "#1b3a6b", color: "#fff", border: "none",
            borderRadius: 10, padding: "9px 20px", fontWeight: 700, fontSize: 13, cursor: "pointer",
          }}>Close</button>
        </div>
      </div>
    </div>
  );
};

const AttendanceReport: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isStaffRole } = useUser();
  // Attendance Report is mounted under one shared route (`/attendance/
  // report/:id`) reached from both Admin and Staff Portal. A Staff
  // user must not be sent to the admin `/student-history/:id` page
  // when clicking Full Profile — they'd bounce off StaffPortalRoute /
  // 403 on the admin student endpoint. Route by role: Staff → the
  // Staff Portal student detail, everyone else → the existing admin
  // /student-history page (unchanged). Belt-and-braces: also treat a
  // URL that happens to sit under `/staff-portal/*` as Staff-context.
  const openStudentProfile = (studentId: number | string) => {
    const useStaffRoute = isStaffRole || location.pathname.startsWith("/staff-portal");
    navigate(useStaffRoute
      ? `/staff-portal/students/${studentId}`
      : `/student-history/${studentId}`);
  };
  // `?return_url=` carries the origin surface (Today, Assessments, an
  // admin schedule list, etc.) so the Back button lands the user on
  // the same view + filters they came from. Falls back to the
  // aggregate report list, matching the pre-existing default.
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get("return_url") || null;
  const goBack = () => {
    // Only accept same-origin relative paths — never open a
    // caller-supplied absolute URL, matching the parent-portal
    // return_url safety pattern established earlier this project.
    if (returnUrl && returnUrl.startsWith("/") && !returnUrl.startsWith("//")) {
      navigate(returnUrl);
    } else {
      navigate("/attendance-record/report");
    }
  };
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);
  const [subjectsModalOpen, setSubjectsModalOpen] = useState(false);
  const [slipStudent, setSlipStudent] = useState<StudentRow | null>(null);

  const { data, isLoading, isError } = useGetAttendanceSessionReportQuery(
    { sessionId: id ?? "" },
    { skip: !id }
  );
  const report: Report | null = useMemo(() => (data?.data as Report) ?? null, [data]);
  const selected = useMemo(
    () => report?.students.find((s) => s.id === selectedStudentId) ?? null,
    [report, selectedStudentId]
  );

  if (isLoading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f0f4f8" }}>
        <StepUpSpinner size="medium" />
      </div>
    );
  }
  if (isError || !report) {
    return (
      <div style={{ minHeight: "100vh", background: "#f0f4f8", padding: 32 }}>
        <button onClick={goBack} style={{ background: "#1b3a6b", color: "#fff", border: "none", borderRadius: 10, padding: "10px 18px", fontWeight: 700, cursor: "pointer" }}>← Back</button>
        <div style={{ marginTop: 20, background: "#fff", borderRadius: 14, padding: 32, textAlign: "center", color: "#b91c1c", fontWeight: 700 }}>
          Failed to load the attendance report.
        </div>
      </div>
    );
  }

  // `top_classes` still in the DTO but unused after the "Highest Class
  // Attendance" card was removed. Not destructured here to keep the
  // linter happy; consumers wanting it can read `report.top_classes`.
  const { session, summary, students, class_trend, top_students } = report;
  const classTitle = summarizeClassTitle(session);

  // Duration + scheduled-vs-actual timeline math.
  const schedStart = new Date(session.scheduled_start_at).getTime();
  const schedEnd = new Date(session.scheduled_end_at).getTime();
  const schedDur = schedEnd - schedStart;
  const actStart = session.actual_start_at ? new Date(session.actual_start_at).getTime() : null;
  const actEnd = session.actual_end_at ? new Date(session.actual_end_at).getTime() : null;
  const lateStartMs = actStart != null ? Math.max(0, actStart - schedStart) : 0;

  // Status chip label.
  const statusLabel = (() => {
    if (session.status === "cancelled") return { text: "Cancelled", bg: "#fee2e2", fg: "#991b1b", border: "#fecaca" };
    if (session.status === "completed") return { text: "Completed", bg: "#d1fae5", fg: "#065f46", border: "#a7f3d0" };
    if (actStart != null) return { text: "In Progress", bg: "#dbeafe", fg: "#1e40af", border: "#bfdbfe" };
    return { text: "Not Started", bg: "#f1f5f9", fg: "#475569", border: "#e2e8f0" };
  })();
  const submittedChip = summary.submitted
    ? { text: "Attendance Submitted", bg: "#eef2ff", fg: "#1e40af", border: "#c7d2fe" }
    : { text: "Attendance Not Recorded", bg: "#f1f5f9", fg: "#64748b", border: "#e2e8f0" };
  const unmarkedChip = summary.submitted && summary.not_recorded > 0
    ? { text: `${summary.not_recorded} unmarked`, bg: "#fffbeb", fg: "#92400e", border: "#fde68a" }
    : null;

  // KPI tiles (spec §8).
  const kpis: Array<{ label: string; value: React.ReactNode; color: string; bg: string; onClick?: () => void }> = [
    { label: "Enrolled",         value: summary.enrolled,       color: "#1b3a6b", bg: "#eef2ff" },
    { label: "Present",          value: summary.present,        color: "#16a34a", bg: "#f0fdf4" },
    { label: "Absent",           value: summary.absent,         color: "#dc2626", bg: "#fef2f2" },
    { label: "Late",             value: summary.late,           color: "#d97706", bg: "#fffbeb" },
    { label: "Not Recorded",     value: summary.submitted ? summary.not_recorded : "—", color: "#64748b", bg: "#f1f5f9" },
    { label: "Late Slips",       value: summary.late_slip_count, color: "#7c3aed", bg: "#f5f3ff" },
    { label: "Attendance Rate",  value: summary.submitted ? `${summary.attendance_rate}%` : "—", color: "#0d9488", bg: "#f0fdfa" },
  ];

  // Trend: exclude unsubmitted (spec §18-19).
  const trendSubmitted = class_trend.filter((t) => t.submitted);
  const showTrend = trendSubmitted.length >= 2;

  return (
    <div style={{ background: "#f0f4f8", minHeight: "100vh", fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif" }}>
      {/* Print-only styles: strip navy backdrops, hide interactive chrome. */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff; }
          .report-root { background: #fff !important; }
          .report-hero { background: #fff !important; color: #0f172a !important; }
          .report-hero * { color: #0f172a !important; }
          .kpi-tile { break-inside: avoid; }
          .student-row { break-inside: avoid; }
        }
      `}</style>

      <div className="report-root">
        {/* ── HERO ── */}
        <div className="report-hero" style={{
          background: "linear-gradient(180deg,#0f172a,#1b3a6b)",
          padding: "24px 32px", color: "#fff",
        }}>
          <div className="no-print" style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
            <button onClick={goBack} style={{
              background: "rgba(255,255,255,0.1)", border: "none",
              color: "#fff", width: 36, height: 36, borderRadius: 8,
              cursor: "pointer", fontSize: 18,
            }}>←</button>
            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 11, letterSpacing: "0.8px", textTransform: "uppercase", fontWeight: 700 }}>
              Attendance Report
            </div>
            <div style={{ flex: 1 }} />
            <button onClick={() => window.print()} style={{
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.3)",
              color: "#fff", borderRadius: 10, padding: "9px 18px",
              fontWeight: 600, fontSize: 13, cursor: "pointer",
            }}>🖨️ Print Report</button>
          </div>

          <div style={{ display: "flex", alignItems: "flex-start", gap: 24, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: "-0.01em", lineHeight: 1.15 }}>
                {classTitle}
              </div>
              <div style={{ fontSize: 13.5, color: "rgba(255,255,255,0.8)", marginTop: 6 }}>{fmtDate(session.date)}</div>

              {/* Teacher / Location / Mode row */}
              <div style={{ display: "flex", gap: 22, flexWrap: "wrap", marginTop: 14 }}>
                <div>
                  <div style={{ fontSize: 10.5, letterSpacing: "0.6px", color: "rgba(255,255,255,0.55)", textTransform: "uppercase", fontWeight: 700 }}>Teacher</div>
                  <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2 }}>{session.staff_name || "—"}</div>
                </div>
                {session.location && (
                  <div>
                    <div style={{ fontSize: 10.5, letterSpacing: "0.6px", color: "rgba(255,255,255,0.55)", textTransform: "uppercase", fontWeight: 700 }}>Location</div>
                    <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2 }}>{session.location}</div>
                  </div>
                )}
                {session.mode && (
                  <div>
                    <div style={{ fontSize: 10.5, letterSpacing: "0.6px", color: "rgba(255,255,255,0.55)", textTransform: "uppercase", fontWeight: 700 }}>Mode</div>
                    <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2 }}>{session.mode}</div>
                  </div>
                )}
              </div>

              {/* Status chips */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 }}>
                {[statusLabel, submittedChip, unmarkedChip].filter(Boolean).map((c, i) => c && (
                  <span key={i} style={{
                    background: c.bg, color: c.fg, border: `1px solid ${c.border}`,
                    borderRadius: 999, padding: "4px 12px", fontSize: 11.5, fontWeight: 700, letterSpacing: "0.2px",
                  }}>{c.text}</span>
                ))}
                {(session.grade_subjects?.length ?? 0) > 0 && (
                  <button onClick={() => setSubjectsModalOpen(true)} className="no-print" style={{
                    background: "rgba(255,255,255,0.14)", color: "#fff",
                    border: "1px solid rgba(255,255,255,0.28)", borderRadius: 999,
                    padding: "4px 12px", fontSize: 11.5, fontWeight: 700, cursor: "pointer",
                  }}>📚 View Grades &amp; Subjects</button>
                )}
              </div>
            </div>

            {/* Scheduled vs Actual card */}
            <div style={{
              minWidth: 320, background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.14)",
              borderRadius: 14, padding: "16px 18px",
            }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
                <div>
                  <div style={{ fontSize: 10.5, letterSpacing: "0.6px", color: "rgba(255,255,255,0.55)", textTransform: "uppercase", fontWeight: 700 }}>Scheduled</div>
                  <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>
                    {fmtTimeIso(session.scheduled_start_at)} – {fmtTimeIso(session.scheduled_end_at)}
                  </div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.65)", marginTop: 2 }}>{fmtDuration(schedDur)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10.5, letterSpacing: "0.6px", color: "rgba(255,255,255,0.55)", textTransform: "uppercase", fontWeight: 700 }}>Actual</div>
                  <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>
                    {actStart ? fmtTimeIso(session.actual_start_at) : "—"} – {actEnd ? fmtTimeIso(session.actual_end_at) : "—"}
                  </div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.65)", marginTop: 2 }}>
                    {actStart == null ? "Not started" :
                      lateStartMs >= 60000 ? `Started ${fmtDuration(lateStartMs)} late` :
                      "On time"}
                  </div>
                </div>
              </div>

              {/* Scheduled vs Actual timeline (CSS only, spec §6) */}
              {actStart != null && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ height: 4, background: "rgba(255,255,255,0.18)", borderRadius: 3, position: "relative" }}>
                    <div style={{ position: "absolute", left: 0, right: 0, top: 0, bottom: 0, background: "rgba(255,255,255,0.35)", borderRadius: 3 }} />
                  </div>
                  <div style={{ height: 4, background: "rgba(255,255,255,0.18)", borderRadius: 3, marginTop: 6, position: "relative" }}>
                    {(() => {
                      const spanStart = Math.min(schedStart, actStart);
                      const spanEnd = Math.max(schedEnd, actEnd ?? schedEnd);
                      const span = Math.max(1, spanEnd - spanStart);
                      const left = ((actStart - spanStart) / span) * 100;
                      const right = 100 - (((actEnd ?? schedEnd) - spanStart) / span) * 100;
                      return (
                        <div style={{
                          position: "absolute", top: 0, bottom: 0,
                          left: `${left}%`, right: `${right}%`,
                          background: lateStartMs >= 60000 ? "#f59e0b" : "#22c55e", borderRadius: 3,
                        }} />
                      );
                    })()}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, color: "rgba(255,255,255,0.65)", marginTop: 4 }}>
                    <span>Scheduled</span>
                    <span>Actual</span>
                  </div>
                </div>
              )}

              {session.ended_by && actEnd && (
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.72)", marginTop: 10 }}>
                  {session.ended_by === "system"
                    ? `Auto-completed at ${fmtTimeIso(session.actual_end_at)}`
                    : `Ended manually at ${fmtTimeIso(session.actual_end_at)}`}
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={{ padding: "24px 32px" }}>
          {/* ── KPI ROW ── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 24 }}>
            {kpis.map((k) => (
              <div key={k.label} className="kpi-tile" style={{
                background: k.bg, borderRadius: 14, padding: "16px 18px",
                border: `1px solid ${k.color}22`, boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                cursor: k.onClick ? "pointer" : "default",
              }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: k.color, lineHeight: 1 }}>{k.value}</div>
                <div style={{ fontSize: 10, fontWeight: 700, color: k.color, opacity: 0.75, letterSpacing: "0.6px", textTransform: "uppercase", marginTop: 6 }}>{k.label}</div>
              </div>
            ))}
          </div>

          {/* ── STUDENT ATTENDANCE TABLE ── */}
          <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", marginBottom: 24 }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 15, color: "#1b3a6b" }}>Student Attendance</div>
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
                  {students.length === 0
                    ? "No students were enrolled in this session."
                    : summary.submitted
                      ? `${summary.enrolled} enrolled · ${summary.present + summary.late} attended · ${summary.absent} absent${summary.not_recorded > 0 ? ` · ${summary.not_recorded} unmarked` : ""}`
                      : "Attendance has not been submitted for this session."}
                </div>
              </div>
            </div>

            {students.length === 0 ? (
              <div style={{ padding: 32, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                No students were enrolled in this session.
              </div>
            ) : (
              <>
                {/* Column headers (desktop) */}
                <div className="no-print" style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0,2fr) 1fr 130px 100px 90px 110px 90px",
                  gap: 12, padding: "10px 20px",
                  background: "#f8fafc", fontSize: 10.5, fontWeight: 700,
                  color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px",
                  borderBottom: "1px solid #e2e8f0",
                }}>
                  <div>Student</div><div>Grade</div><div>Status</div>
                  <div>Arrival</div><div>Late</div><div>Late Slip</div><div>Overall</div>
                </div>

                {students.map((s) => {
                  const st = statusChip(s.status);
                  const rc = rateColor(s.attendance_rate);
                  const isSel = selectedStudentId === s.id;
                  return (
                    <div key={s.id} className="student-row" onClick={() => setSelectedStudentId(isSel ? null : s.id)}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0,2fr) 1fr 130px 100px 90px 110px 90px",
                        gap: 12, padding: "12px 20px", alignItems: "center",
                        borderBottom: "1px solid #f1f5f9", cursor: "pointer",
                        background: isSel ? "#f0f4ff" : "#fff",
                        fontSize: 13,
                      }}
                      onMouseEnter={(e) => ((e.currentTarget as HTMLDivElement).style.background = isSel ? "#f0f4ff" : "#f8fafc")}
                      onMouseLeave={(e) => ((e.currentTarget as HTMLDivElement).style.background = isSel ? "#f0f4ff" : "#fff")}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                        <img src={getAvatar(s.name, s.avatar, 60)} alt="" style={{ width: 32, height: 32, borderRadius: "50%", border: "1px solid #e2e8f0", objectFit: "cover", flexShrink: 0 }} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: "#1a1a2e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</div>
                          {s.parent_name && <div style={{ fontSize: 11, color: "#94a3b8" }}>Parent: {s.parent_name}</div>}
                        </div>
                      </div>
                      <div style={{ color: "#475569" }}>{s.grade || "—"}</div>
                      <div>
                        <span style={{
                          background: st.bg, color: st.fg, border: `1px solid ${st.border}`,
                          borderRadius: 999, padding: "3px 10px", fontSize: 11.5, fontWeight: 700,
                        }}>{st.label}</span>
                      </div>
                      <div style={{ color: s.arrived_at ? "#0f172a" : "#94a3b8" }}>
                        {s.arrived_at ? fmtTimeIso(s.arrived_at) : "—"}
                      </div>
                      {(() => {
                        const lateLabel = formatLateMinutes(s.late_minutes);
                        return (
                          <div style={{
                            color: lateLabel ? "#d97706" : "#94a3b8",
                            fontWeight: lateLabel ? 700 : 500,
                            whiteSpace: "nowrap",
                          }}>
                            {lateLabel ?? "—"}
                          </div>
                        );
                      })()}
                      <div>
                        {s.has_late_slip ? (
                          <button onClick={(e) => { e.stopPropagation(); setSlipStudent(s); }} className="no-print" style={{
                            background: "#f5f3ff", color: "#7c3aed", border: "1px solid #ddd6fe",
                            borderRadius: 8, padding: "4px 10px", fontSize: 11.5, fontWeight: 700, cursor: "pointer",
                          }}>View Slip</button>
                        ) : <span style={{ color: "#94a3b8" }}>—</span>}
                      </div>
                      <div style={{ textAlign: "right", fontWeight: 800, color: rc, fontSize: 15 }}>{s.attendance_rate}%</div>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {/* ── SELECTED STUDENT DETAIL DRAWER ── */}
          {selected && (
            <div className="no-print" style={{
              background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0",
              overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", marginBottom: 24,
            }}>
              <div style={{ background: "#1b3a6b", padding: "16px 22px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                <img src={getAvatar(selected.name, selected.avatar, 96)} alt="" style={{
                  width: 46, height: 46, borderRadius: "50%",
                  border: "2px solid rgba(255,255,255,0.3)", objectFit: "cover",
                }} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>{selected.name}</div>
                  <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 12 }}>
                    {selected.grade || "—"}{selected.parent_name ? ` · Parent: ${selected.parent_name}` : ""}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 32, fontWeight: 900, color: "#fff", lineHeight: 1 }}>{selected.attendance_rate}%</div>
                  <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 11 }}>Overall</div>
                </div>
                <button
                  onClick={() => openStudentProfile(selected.id)}
                  title="Open student profile"
                  style={{
                    background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)",
                    color: "#fff", borderRadius: 10, padding: "8px 14px", fontWeight: 600, fontSize: 12, cursor: "pointer",
                  }}
                >Full Profile</button>
                <button onClick={() => setSelectedStudentId(null)} style={{
                  background: "rgba(255,255,255,0.1)", border: "none",
                  color: "#fff", width: 30, height: 30, borderRadius: 8, cursor: "pointer", fontSize: 15,
                }}>✕</button>
              </div>
              <div style={{ padding: "18px 22px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10, marginBottom: 18 }}>
                  {[
                    { label: "Total", value: selected.stats.total, color: "#1b3a6b" },
                    { label: "Present", value: selected.stats.present, color: "#16a34a" },
                    { label: "Absent", value: selected.stats.absent, color: "#dc2626" },
                    // COUNT tile — `stats.late` is the number of sessions
                    // this student was marked Late (not a duration). Labeled
                    // "Late Times" so it can never read as `0m`; a duration
                    // would use formatLateMinutes + em-dash instead.
                    { label: "Late Times", value: selected.stats.late, color: "#d97706" },
                    { label: "This Month", value: `${selected.stats.this_month_rate}%`, color: "#0891b2" },
                    { label: "Last Month", value: `${selected.stats.last_month_rate}%`, color: "#7c3aed" },
                  ].map((c) => (
                    <div key={c.label} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px" }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: c.color }}>{c.value}</div>
                      <div style={{ fontSize: 10, color: c.color, opacity: 0.7, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", marginTop: 2 }}>{c.label}</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#1b3a6b", marginBottom: 8 }}>Recent Classes</div>
                {selected.recent_history.length === 0 ? (
                  <div style={{ fontSize: 12, color: "#94a3b8" }}>No recent history.</div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8 }}>
                    {selected.recent_history.slice(0, 10).map((h, i) => {
                      const dot = h.status === "present" ? "#16a34a" : h.status === "absent" ? "#dc2626" : h.status === "late" ? "#d97706" : "#e2e8f0";
                      return (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fafafa" }}>
                          <div style={{ width: 8, height: 8, borderRadius: "50%", background: dot, flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "#1a1a2e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.class_name}</div>
                            <div style={{ fontSize: 10, color: "#94a3b8" }}>{h.date}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── TREND (only if enough submitted sessions) ── */}
          <div className="no-print" style={{ background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0", padding: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", marginBottom: 24 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: "#1b3a6b" }}>Attendance Trend</div>
            <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 16 }}>
              {showTrend ? `Last ${trendSubmitted.length} submitted session${trendSubmitted.length === 1 ? "" : "s"} for this class` : ""}
            </div>
            {!showTrend ? (
              <div style={{ padding: "20px 8px", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                Not enough submitted attendance data to show a trend.
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 180, borderBottom: "1px solid #e2e8f0", paddingBottom: 4 }}>
                {trendSubmitted.map((t, i) => {
                  const h = Math.max(2, Math.round((t.rate / 100) * 150));
                  const color = t.isCurrentSession ? "#1b3a6b" : rateColor(t.rate);
                  return (
                    <div key={i} title={`${t.label}: ${t.rate}%`} style={{
                      flex: 1, minWidth: 0, display: "flex", flexDirection: "column",
                      alignItems: "center", justifyContent: "flex-end", height: "100%",
                    }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#334155", marginBottom: 3 }}>{t.rate}%</div>
                      <div style={{
                        width: "80%", height: h, background: color, borderRadius: "6px 6px 0 0",
                        border: t.isCurrentSession ? "2px solid #0b2547" : "none",
                      }} />
                      <div style={{ fontSize: 10, color: "#64748b", marginTop: 4, textAlign: "center" }}>{t.label}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Demoted rankings (spec §21) — Top Students collapsed at
                page bottom. Highest Class Attendance card removed per
                product request; backend `top_classes` field is kept in
                the DTO in case a future card / print-only summary
                consumes it. */}
          {top_students.length > 0 && (
            <div className="no-print" style={{ marginBottom: 24 }}>
              <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden" }}>
                <div style={{ padding: "12px 16px", borderBottom: "1px solid #e2e8f0", fontSize: 12, fontWeight: 700, color: "#64748b", letterSpacing: "0.5px", textTransform: "uppercase" }}>Top Students · This Class</div>
                {top_students.slice(0, 5).map((s, i) => (
                  <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderBottom: "1px solid #f1f5f9", fontSize: 12 }}>
                    <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#eef2ff", color: "#1b3a6b", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800 }}>{i + 1}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</div>
                      <div style={{ fontSize: 10.5, color: "#94a3b8" }}>{s.grade || "—"}</div>
                    </div>
                    <div style={{ fontWeight: 800, color: "#16a34a" }}>{s.attendance_rate}%</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <GradeSubjectsDialog
        open={subjectsModalOpen}
        onClose={() => setSubjectsModalOpen(false)}
        groups={session.grade_subjects || []}
        title="Grades & Subjects"
        subtitle={[
          session.staff_name ? `Teacher: ${session.staff_name}` : null,
          fmtDate(session.date),
          `${fmtTimeIso(session.scheduled_start_at)} – ${fmtTimeIso(session.scheduled_end_at)}`,
          `${summary.enrolled} student${summary.enrolled === 1 ? "" : "s"}`,
        ].filter(Boolean).join(" · ")}
      />
      <LateSlipModal student={slipStudent} session={session} onClose={() => setSlipStudent(null)} />
    </div>
  );
};

export default AttendanceReport;
